import OneSignal from 'react-onesignal';

const OneSignalService = {
    /**
     * Initializes the OneSignal SDK with proper service worker configuration
     * for PWA push notifications (background + foreground).
     * @param {string} appId The OneSignal App ID
     */
    init: async (appId) => {
        if (!appId) {
            console.warn("OneSignal App ID is missing, skipping initialization.");
            return;
        }

        try {
            await OneSignal.init({
                appId: appId,
                allowLocalhostAsSecureOrigin: true,
                // Explicitly tell OneSignal where the service worker is
                serviceWorkerParam: { scope: '/' },
                serviceWorkerPath: '/OneSignalSDKWorker.js',
                // Show native browser prompt automatically
                promptOptions: {
                    slidedown: {
                        prompts: [{
                            type: "push",
                            autoPrompt: true,
                            text: {
                                actionMessage: "Voulez-vous recevoir les notifications de nouveaux messages ?",
                                acceptButton: "Oui",
                                cancelButton: "Plus tard"
                            }
                        }]
                    }
                }
            });

            console.log("OneSignal initialized successfully");

            // Check and request permission
            const permission = OneSignal.Notifications.permission;
            console.log("OneSignal: Current notification permission:", permission);

            if (!permission) {
                console.log("OneSignal: Requesting notification permission...");
                try {
                    const granted = await OneSignal.Notifications.requestPermission();
                    console.log("OneSignal: Permission result:", granted);
                } catch (permError) {
                    console.warn("OneSignal: Permission request deferred:", permError);
                }
            }

            // Log subscription info
            const currentSubId = OneSignal.User.PushSubscription.id;
            const currentToken = OneSignal.User.PushSubscription.token;
            console.log("OneSignal: Subscription ID:", currentSubId);
            console.log("OneSignal: Push token:", currentToken ? "present" : "none");

            // Listen for subscription changes
            OneSignal.User.PushSubscription.addEventListener('change', (event) => {
                console.log("OneSignal: Subscription changed:", event.current?.id);
                console.log("OneSignal: New push token:", event.current?.token ? "present" : "none");
            });

        } catch (error) {
            console.error("OneSignal initialization failed:", error);
        }
    },

    /**
     * Associates the user identity with the OneSignal external user ID.
     * @param {string} userId The Supabase user ID
     */
    login: async (userId) => {
        try {
            await OneSignal.login(userId);
            console.log("OneSignal: User logged in with ID", userId);
        } catch (error) {
            console.error("OneSignal login failed:", error);
        }
    },

    /**
     * Disassociates the user identity on sign out.
     */
    logout: async () => {
        try {
            await OneSignal.logout();
            console.log("OneSignal: User logged out");
        } catch (error) {
            console.error("OneSignal logout failed:", error);
        }
    },

    /**
     * Sets tags on the current OneSignal user.
     * @param {object} tags Key-value pairs
     */
    setTags: async (tags) => {
        try {
            if (OneSignal.User && OneSignal.User.addTags) {
                await OneSignal.User.addTags(tags);
            }
        } catch (error) {
            console.error("OneSignal setTags failed:", error);
        }
    },

    /**
     * Controls the logging levels of OneSignal.
     * @param {boolean} isDebug Enable/disable debug logs
     */
    setDebug: (isDebug) => {
        try {
            if (OneSignal.Debug) {
                OneSignal.Debug.setLogLevel(isDebug ? 6 : 0);
            }
        } catch (error) {
            console.error("OneSignal setDebug failed:", error);
        }
    }
};

export default OneSignalService;
